import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Package, AlertCircle, CheckCircle } from 'lucide-react';
import CategoryEditModal from './CategoryEditModal';
import BulkSubcategoryModal from './BulkSubcategoryModal';
import { renderCategoryIcon } from './IconSelector';
import { CategoryHierarchy } from '../../types/category';
import { getItemTrackingConfig } from '../../utils/categoryTracking';

interface CategoryTreeNode extends CategoryHierarchy {
  children: CategoryTreeNode[];
  level: number; // 0 = parent, 1 = child
}

interface CategoryManagerProps {
  categories: CategoryHierarchy[];
  onCategoryCreate: (category: CategoryHierarchy) => void;
  onCategoryUpdate: (category: CategoryHierarchy) => void;
  onCategoryDelete: (categoryId: string) => void;
  onBulkCreate: (parentId: string, subcategories: string[]) => void;
  isLoading?: boolean;
}

const CategoryManager: React.FC<CategoryManagerProps> = ({
  categories,
  onCategoryCreate,
  onCategoryUpdate,
  onCategoryDelete,
  onBulkCreate,
  isLoading = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<CategoryHierarchy | null>(null);
  const [selectedParent, setSelectedParent] = useState<CategoryHierarchy | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // Build category tree structure
  const buildCategoryTree = (categories: CategoryHierarchy[]): CategoryTreeNode[] => {
    const tree: CategoryTreeNode[] = [];
    
    // Get parent categories first
    const parents = categories.filter(cat => !cat.parentCategoryId);
    
    parents.forEach(parent => {
      const children = categories
        .filter(cat => cat.parentCategoryId === parent.categoryId)
        .map(child => ({ ...child, children: [], level: 1 }));
      
      tree.push({
        ...parent,
        children,
        level: 0
      });
    });
    
    return tree;
  };

  // Filter categories based on search
  const filteredTree = buildCategoryTree(categories).filter(node => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const nameMatch = node.name.toLowerCase().includes(searchLower);
    const childMatch = node.children.some(child => 
      child.name.toLowerCase().includes(searchLower)
    );
    
    return nameMatch || childMatch;
  });

  const handleCreateCategory = () => {
    setSelectedCategory(null);
    setSelectedParent(null);
    setIsCreating(true);
    setIsEditModalOpen(true);
  };

  const handleCreateSubcategory = (parent: CategoryHierarchy) => {
    setSelectedCategory(null);
    setSelectedParent(parent);
    setIsCreating(true);
    setIsEditModalOpen(true);
  };

  const handleEditCategory = (category: CategoryHierarchy) => {
    setSelectedCategory(category);
    setSelectedParent(null);
    setIsCreating(false);
    setIsEditModalOpen(true);
  };

  const handleBulkCreate = (parent: CategoryHierarchy) => {
    setSelectedParent(parent);
    setIsBulkModalOpen(true);
  };

  const handleModalClose = () => {
    setIsEditModalOpen(false);
    setIsBulkModalOpen(false);
    setSelectedCategory(null);
    setSelectedParent(null);
    setIsCreating(false);
  };

  // Handler for bulk subcategory creation
  const handleBulkSubcategorySave = async (subcategories: { name: string; description: string }[]) => {
    if (!selectedParent) return;
    
    const subcategoryNames = subcategories.map(sub => sub.name);
    await onBulkCreate(selectedParent.categoryId, subcategoryNames);
  };

  const CategoryTreeItem: React.FC<{ node: CategoryTreeNode }> = ({ node }) => {
    const isParent = node.level === 0;
    const isSubcategory = node.level === 1;
    
    // Use helper to get tracking config (handles both old and new formats)
    const trackingConfig = isSubcategory ? getItemTrackingConfig(node) : null;
    
    return (
      <div className="border border-gray-200 rounded-lg mb-2">
        {/* Parent/Subcategory Row */}
        <div className="flex items-center justify-between p-4 hover:bg-gray-50">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isParent ? 'bg-blue-100' : 'bg-gray-100'}`}>
              {node.icon ? (
                renderCategoryIcon(node.icon, `h-5 w-5 ${isParent ? 'text-blue-600' : 'text-gray-600'}`)
              ) : (
                <Package className={`h-5 w-5 ${isParent ? 'text-blue-600' : 'text-gray-600'}`} />
              )}
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-900 flex items-center">
                {node.name}
                
                {/* Tracking indicators for subcategories */}
                {isSubcategory && trackingConfig && (
                  <>
                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                      trackingConfig.trackingType === 'SERIALIZED'
                        ? 'bg-blue-100 text-blue-800' 
                        : trackingConfig.trackingType === 'CONSUMABLE'
                          ? 'bg-orange-100 text-orange-800'
                          : 'bg-green-100 text-green-800'
                    }`}>
                      {trackingConfig.trackingType === 'SERIALIZED' 
                        ? 'Serialized' 
                        : trackingConfig.trackingType === 'CONSUMABLE'
                          ? 'Consumable'
                          : 'Bulk Tracked'}
                    </span>
                  </>
                )}
                
                {/* Organizational indicator for parents */}
                {isParent && (
                  <span className="ml-2 px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">
                    Organizational
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500">{node.description}</p>
              {node.itemCount !== undefined && (
                <p className="text-xs text-gray-400 mt-1">
                  {node.itemCount} items
                </p>
              )}
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Parent category actions */}
            {isParent && (
              <>
                <button
                  onClick={() => handleCreateSubcategory(node)}
                  className="px-3 py-1 text-sm text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition-colors"
                >
                  Add Subcategory
                </button>
                <button
                  onClick={() => handleBulkCreate(node)}
                  className="px-3 py-1 text-sm text-green-600 hover:text-green-700 hover:bg-green-50 rounded transition-colors"
                >
                  Bulk Create
                </button>
              </>
            )}
            
            {/* Edit button for all categories */}
            <button
              onClick={() => handleEditCategory(node)}
              className="p-2 text-gray-600 hover:text-gray-700 hover:bg-gray-50 rounded transition-colors"
            >
              <Edit2 className="h-4 w-4" />
            </button>
            
            {/* Delete button for all categories */}
            <button
              onClick={() => onCategoryDelete(node.categoryId)}
              className="p-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-colors"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>
        
        {/* Child categories */}
        {node.children.length > 0 && (
          <div className="border-t border-gray-200 bg-gray-50">
            <div className="px-4 py-2">
              <h4 className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
                Subcategories ({node.children.length})
              </h4>
              <div className="space-y-2">
                {node.children.map(child => (
                  <CategoryTreeItem key={child.categoryId} node={child} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Category Management</h2>
          <p className="text-sm text-gray-600 mt-1">
            Organize inventory items into categories and subcategories
          </p>
        </div>
        <button 
          onClick={handleCreateCategory} 
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          <span>Create Category</span>
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          type="text"
          placeholder="Search categories..."
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>

      {/* Category Tree */}
      <div className="space-y-4">
        {filteredTree.length === 0 ? (
          <div className="text-center py-12">
            <Package className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              {searchTerm ? 'No categories found' : 'No categories yet'}
            </h3>
            <p className="text-gray-600 mb-6">
              {searchTerm 
                ? 'Try adjusting your search terms'
                : 'Create your first category to get started organizing inventory'
              }
            </p>
            {!searchTerm && (
              <button 
                onClick={handleCreateCategory} 
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors mx-auto"
              >
                <Plus className="h-4 w-4" />
                <span>Create First Category</span>
              </button>
            )}
          </div>
        ) : (
          <div>
            {filteredTree.map(node => (
              <CategoryTreeItem key={node.categoryId} node={node} />
            ))}
          </div>
        )}
      </div>

      {/* Category Statistics */}
      {categories.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-700 mb-2">Statistics</h3>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-gray-600">Total Categories:</span>
              <span className="font-medium ml-2">{categories.length}</span>
            </div>
            <div>
              <span className="text-gray-600">Parent Categories:</span>
              <span className="font-medium ml-2">
                {categories.filter(cat => !cat.parentCategoryId).length}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Subcategories:</span>
              <span className="font-medium ml-2">
                {categories.filter(cat => cat.parentCategoryId).length}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      <CategoryEditModal
        isOpen={isEditModalOpen}
        onClose={handleModalClose}
        category={selectedCategory}
        parentCategories={categories.filter(cat => !cat.parentCategoryId)}
        onSave={async (data) => {
          if (isCreating) {
            const newCategory: CategoryHierarchy = {
              categoryId: `temp_${Date.now()}`,
              name: data.name || '',
              description: data.description || '',
              icon: data.icon || 'package',
              sortOrder: categories.length + 1,
              isActive: data.isActive ?? true,
              parentCategoryId: selectedParent?.categoryId || (data as any).parentCategoryId || null,
              // NEW: Include itemTrackingConfig if provided
              itemTrackingConfig: data.itemTrackingConfig,
              // DEPRECATED: Keep bulkItemBehavior for backward compatibility
              bulkItemBehavior: data.bulkItemBehavior
            };
            onCategoryCreate(newCategory);
          } else if (selectedCategory) {
            const updatedCategory: CategoryHierarchy = {
              ...selectedCategory,
              name: data.name || selectedCategory.name,
              description: data.description || selectedCategory.description || '',
              icon: data.icon || selectedCategory.icon,
              isActive: data.isActive ?? selectedCategory.isActive,
              // NEW: Update itemTrackingConfig if provided
              itemTrackingConfig: data.itemTrackingConfig || selectedCategory.itemTrackingConfig,
              // DEPRECATED: Keep bulkItemBehavior for backward compatibility
              bulkItemBehavior: data.bulkItemBehavior || selectedCategory.bulkItemBehavior
            };
            onCategoryUpdate(updatedCategory);
          }
        }}
      />

      <BulkSubcategoryModal
        isOpen={isBulkModalOpen}
        onClose={handleModalClose}
        parentCategory={selectedParent ? {
          ...selectedParent,
          children: [],
          level: 0
        } : null}
        onSave={handleBulkSubcategorySave}
      />
    </div>
  );
};

export default CategoryManager;
